'use client';

import { useState, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslations } from 'next-intl';
import { ChatBubble } from '@boty/ui';
import type { useIntakeFlow } from './useIntakeFlow';

type IntakeFlowReturn = ReturnType<typeof useIntakeFlow>;

interface IntakeQuestionsFlowProps {
  creatorName: string;
  flow: IntakeFlowReturn;
}

export function IntakeQuestionsFlow({
  creatorName: _creatorName,
  flow,
}: IntakeQuestionsFlowProps) {
  const t = useTranslations('consumer.intake');
  const {
    state,
    questions,
    currentQuestion,
    promptMessage,
    completionMessage,
    isSubmitting,
    acceptPrompt,
    dismissPrompt,
    answerQuestion,
    skipQuestion,
    confirmSendToCreator,
    declineSend,
    goBack,
  } = flow;

  const isLastQuestion = state.currentQuestionIndex === questions.length - 1;

  const [inputValue, setInputValue] = useState('');

  // Restore previous answer when navigating back to a question
  useEffect(() => {
    if (state.status === 'asking' && currentQuestion) {
      const previousAnswer = state.answers.get(currentQuestion.id);
      setInputValue(previousAnswer || '');
    }
  }, [state.status, currentQuestion, state.answers]);

  const handleSubmitAnswer = useCallback(() => {
    if (!inputValue.trim()) return;
    answerQuestion(inputValue.trim());
    setInputValue('');
  }, [inputValue, answerQuestion]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmitAnswer();
      }
    },
    [handleSubmitAnswer]
  );

  if (state.status === 'idle' || state.status === 'dismissed') {
    return null;
  }

  if (state.status === 'prompting') {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <ChatBubble sender="bot">
          {promptMessage || t('prompt')}
        </ChatBubble>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, ml: 6 }}>
          <Button variant="contained" size="small" onClick={acceptPrompt}>
            {t('accept')}
          </Button>
          <Button variant="outlined" size="small" onClick={dismissPrompt}>
            {t('dismiss')}
          </Button>
        </Box>
      </Box>
    );
  }

  if (state.status === 'asking' && currentQuestion) {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <ChatBubble sender="bot">
          {currentQuestion.questionText}
        </ChatBubble>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, ml: 6 }}>
          <TextField
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('typeAnswer', { defaultMessage: 'Type your answer...' })}
            size="small"
            fullWidth
            autoFocus
            disabled={isSubmitting}
            inputProps={{ maxLength: 2000 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmitAnswer}
            disabled={!inputValue.trim() || isSubmitting}
            aria-label={isLastQuestion ? t('sendAnswer') : t('submitAnswer', { defaultMessage: 'Submit' })}
          >
            {isSubmitting ? <CircularProgress size={16} /> : isLastQuestion ? t('sendAnswer') : '→'}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, ml: 6, mt: 0.5 }}>
          {state.currentQuestionIndex > 0 && (
            <Button size="small" onClick={goBack} disabled={isSubmitting}>
              {t('back')}
            </Button>
          )}
          <Button size="small" onClick={skipQuestion} disabled={isSubmitting}>
            {isLastQuestion ? t('skipAndSend') : t('skipQuestion')}
          </Button>
        </Box>
      </Box>
    );
  }

  if (state.status === 'confirming') {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, ml: 6 }}>
          <Button
            variant="contained"
            size="small"
            onClick={confirmSendToCreator}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={16} /> : t('send')}
          </Button>
          <Button variant="outlined" size="small" onClick={declineSend} disabled={isSubmitting}>
            {t('cancel')}
          </Button>
        </Box>
      </Box>
    );
  }

  if (state.status === 'completed' && completionMessage) {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <ChatBubble sender="bot">
          {completionMessage}
        </ChatBubble>
      </Box>
    );
  }

  return null;
}
